import React, { useEffect } from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import Select from 'components/lib/Select';
import { COMPANY_STATUS, COMPANY_STATUS_LABEL } from 'constants/company';
import { useAppSelector } from 'store/hook';
import { CompanyType } from 'types/company';

import { useCommunityAdminContext } from './CommunityAdminContextProvider';

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

const Label = styled.span`
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
  width: 102px;
  text-align: right;
`;

const CompanyNameLabel = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const SelectorLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Status = styled.div`
  font-size: 14px;
  color: var(--text-primary);

  &.trial {
    color: var(--inactive-orange);
  }
  &.outside {
    color: var(--text-secondary);
  }
  &.paying {
    color: var(--positive-green);
  }
`;

const CommunityAdminContentCompanySelector = () => {
  const { companies } = useAppSelector((state) => state.global);
  const { community, selectedCompany, setSelectedCompany, setIncludeInRegularShopCadence } = useCommunityAdminContext();

  useEffect(() => {
    if (community) {
      const communityCompany = companies.find((company) => company.id === community?.company.id);
      setSelectedCompany(communityCompany);
      if (communityCompany?.status !== COMPANY_STATUS.PAYING_CUSTOMER) {
        setIncludeInRegularShopCadence(false);
      }
    }
  }, [community]);

  const companiesOptions = (companies || []).map((company: CompanyType) => ({
    value: company.id,
    name: company.name,
    label: (
      <SelectorLabel>
        <CompanyNameLabel>{company.name}</CompanyNameLabel>
        <span>·</span>
        <Status
          className={classNames({
            outside: COMPANY_STATUS.OUTSIDE_COMPANY === company.status,
            trial: COMPANY_STATUS.IN_TRIAL === company.status,
            paying: COMPANY_STATUS.PAYING_CUSTOMER === company.status,
          })}
        >
          {COMPANY_STATUS_LABEL[company.status]}
        </Status>
      </SelectorLabel>
    ),
  }));

  const handleSelection = (value: number) => {
    const selected = companies.find((company) => company.id === value);
    if (selected) {
      setSelectedCompany(selected);
      setIncludeInRegularShopCadence(selected.status === COMPANY_STATUS.PAYING_CUSTOMER);
    }
  };

  return (
    <Container>
      <Label>Company</Label>
      <Select
        showSearch
        className={'rectangular'}
        filterOption={(input, option) => option?.name?.toLowerCase().includes(input.toLowerCase())}
        disabled={!companiesOptions?.length}
        style={{ width: 342, height: 36 }}
        options={companiesOptions}
        value={selectedCompany?.id}
        onSelect={handleSelection}
        dropdownStyle={{
          padding: 0,
          borderRadius: 0,
          border: '1px solid var(--line-gray)',
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
        }}
      />
    </Container>
  );
};

export default CommunityAdminContentCompanySelector;
